// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Internationalisation — assemblage du catalogue et Provider React.
//
// Ce fichier ne porte PLUS aucune traduction : les 805 clés par langue vivent
// dans `locales/`, un fichier par domaine métier (vague 1, découpe de juillet
// 2026 — il faisait 1 863 lignes).
//
// Ajouter une clé
// ---------------
// 1. Choisir le module de `locales/` correspondant à l'écran concerné. En cas de
//    doute, se fier à l'USAGE réel de la clé, jamais à son préfixe : `plus_*`
//    n'est pas « PRO+ » mais le « plus » français, et `doc_validated` n'est pas
//    un document projet.
// 2. L'ajouter dans `fr` ET dans `en`, dans le même fichier. C'est la raison pour
//    laquelle les deux langues cohabitent : l'oubli se voit dans le diff.
// 3. `tests/unit/test_i18n_parity.py` vérifie la parité fr/en, l'absence de
//    doublon, et compare le catalogue assemblé à `tests/fixtures/i18n_catalogue.json`.
//
// ⚠️ Ce dernier point : la fixture fige l'état AVANT découpe. Toute évolution
// volontaire du catalogue (nouvelle clé, reformulation) demande de la regénérer —
// la commande est dans le docstring du test. C'est un geste conscient, pas un
// contournement.

import React, { createContext, useContext, useState, useEffect } from 'react';

import admin from './locales/admin';
import analyse from './locales/analyse';
import annotations from './locales/annotations';
import commun from './locales/commun';
import compte from './locales/compte';
import entreprise from './locales/entreprise';
import facturation from './locales/facturation';
import navigation from './locales/navigation';
import projets from './locales/projets';
import statistiques from './locales/statistiques';
// Libellés propres à chaque module de norme (T2) : `modules/<norme>/locales.js`.
import { LIBELLES_MODULES } from './modules';

const MODULES = [
  admin, analyse, annotations, commun, compte, entreprise, facturation,
  navigation, projets, statistiques,
  ...LIBELLES_MODULES,
];

// Catalogue assemblé. Les modules sont disjoints — le test de doublon le
// garantit — donc l'ordre de fusion n'a aucune importance.
const catalogue = {
  fr: Object.assign({}, ...MODULES.map((m) => m.fr)),
  en: Object.assign({}, ...MODULES.map((m) => m.en)),
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(localStorage.getItem('norma_lang') || 'fr');

  useEffect(() => {
    localStorage.setItem('norma_lang', lang);
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  // Repli sur le français puis sur la clé elle-même : une clé manquante s'affiche
  // brute à l'écran plutôt que de faire planter le rendu. C'est aussi pourquoi
  // rien ne signalait les oublis avant le test de parité.
  const t = (key) => catalogue[lang]?.[key] ?? catalogue['fr']?.[key] ?? key;

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n doit être utilisé dans I18nProvider');
  return ctx;
};
